import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import type {Workstream} from 'client';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {useHoverShade} from 'components/DSYS/useHoverShade';
import {formatDayAge} from 'helpers/dates';
import {type ReactNode, useEffect, useMemo, useState} from 'react';
import {useWorkstreamDisplayStatus} from 'views/workstreams/hooks';
import {getWorkstreamDri, getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

import {canEditUpdate, EditableUpdatePost} from './EditableUpdatePost';
import {NextUpdatePrompt} from './NextUpdatePrompt';
import {formatUpdateDate, UpdateBox} from './UpdateCard';
import {logWorkstreamsInteraction, type UpdateOpenedFrom} from './workstreamsAnalytics';

// Fits "● Jun 11, 2025"; dates are short.
const LIST_WIDTH_PX = 128;
// The box is as tall as the list, up to this many rows; longer histories
// scroll within it.
const MIN_ROWS = 5;
const ROW_PX = 32;
const ROW_GAP_PX = 4;

// An update id, the 'next' (due) row, or nothing.
type Selection = number | 'next' | undefined;

// A list row: hover / selected tint like the left nav.
const HistoryRow = ({
  selected = false,
  onClick,
  children,
}: {
  selected?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => {
  const [hovered, setHovered] = useState(false);
  const shade = useHoverShade(hovered && !selected);
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(event) => event.preventDefault()}
      className={`${atoms({
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: 'Medium',
        paddingX: '8',
        cursor: 'pointer',
        color: 'inherit',
        backgroundColor: selected ? 'Opacity Surface' : 'transparent',
      })} ${shade.className}`}
      style={{...shade.style, gap: 8, minHeight: 32, border: 0, textAlign: 'left'}}
    >
      {children}
    </button>
  );
};

/**
 * /workstreams detail: updates as a short history list (status dot ·
 * date, newest first) beside the selected update. When an update is due, the
 * list starts with a row for the next one; selecting it shows when it's due and
 * the status button (opens the update modal). With no updates yet, there's no
 * list, only that prompt. The list scrolls inside the box's height (at most
 * MIN_ROWS rows tall). Fills the caller's width.
 */
export const UpdateHistory = ({
  workstream,
  onLeaveUpdate,
}: {
  workstream: Workstream;
  onLeaveUpdate: (id: number, from?: UpdateOpenedFrom) => void;
}) => {
  // Archiving posts an 'archived' update; it isn't part of the history (the
  // title's chip says archived), so the timeline reads as it did before.
  const updates = useMemo(
    () => (workstream.updates ?? []).filter((update) => update.status !== 'archived'),
    [workstream.updates]
  );
  const {showArchivedChip: archived} = useWorkstreamDisplayStatus(workstream);
  const needsUpdate = Boolean(workstream.status_update_needed);
  const daysOverdue =
    typeof workstream.status_update_needed === 'number' ? workstream.status_update_needed : 0;
  // The "next update" row: when one is due, or to write the first one. Archived
  // workstreams aren't owed one.
  const hasNextRow = !archived && (needsUpdate || updates.length === 0);
  const latestId = updates[0]?.id;
  const defaultSelection = (): Selection => latestId ?? (hasNextRow ? 'next' : undefined);
  const [selection, setSelection] = useState<Selection>(defaultSelection);
  // A newly posted update becomes the selection.
  useEffect(() => setSelection(defaultSelection()), [latestId]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (next: Selection) => {
    logWorkstreamsInteraction('update_history_selected', {
      workstream_id: workstream.id,
      row: next === 'next' ? 'due' : next === latestId ? 'latest' : 'older',
    });
    setSelection(next);
  };

  const selectedIndex = updates.findIndex((update) => update.id === selection);
  const selected = selectedIndex >= 0 ? updates[selectedIndex] : undefined;

  const dueDate = workstream.status_due_date
    ? formatUpdateDate(workstream.status_due_date)
    : undefined;
  // With no updates the list is hidden, so the title says it's the first one.
  const noun = updates.length === 0 ? 'First update' : 'Update';
  const nextTitle =
    daysOverdue > 0
      ? `${noun} ${formatDayAge(daysOverdue)} overdue`
      : needsUpdate
        ? dueDate
          ? `${noun} due ${dueDate}`
          : `${noun} due`
        : 'No updates yet';

  const rowCount = updates.length + (hasNextRow ? 1 : 0);
  const visibleRows = Math.min(MIN_ROWS, Math.max(rowCount, 1));
  const minHeight = visibleRows * ROW_PX + (visibleRows - 1) * ROW_GAP_PX;
  // With no updates there's nothing to pick between: no list, just the prompt
  // (it carries the due date), full width like the sections below.
  const showList = updates.length > 0;

  return (
    <Stack gap="20">
      {/* The box sets the height; the list fills it and scrolls. */}
      <Box display="flex" alignItems="stretch" style={{gap: 16, minWidth: 0}}>
        {/* Absolutely filled, so the list never adds height of its own. */}
        {showList && (
          <Box style={{position: 'relative', width: LIST_WIDTH_PX, flexShrink: 0}}>
            <Box
              display="flex"
              flexDirection="column"
              style={{position: 'absolute', inset: 0, overflowY: 'auto', gap: ROW_GAP_PX}}
            >
              {hasNextRow && (
                <HistoryRow selected={selection === 'next'} onClick={() => pick('next')}>
                  <StatusButtonIcon status="no_status" size="xsmall" includeArchived />
                  <Text
                    size="small"
                    isBold={selection === 'next'}
                    color={daysOverdue > 0 ? 'error' : 'subtle'}
                  >
                    {dueDate ?? 'Due'}
                  </Text>
                </HistoryRow>
              )}
              {updates.map((update) => (
                <HistoryRow
                  key={update.id}
                  selected={update.id === selection}
                  onClick={() => pick(update.id)}
                >
                  <StatusButtonIcon
                    status={getWorkstreamUpdateVisualStatus(update.status)}
                    size="xsmall"
                    includeArchived
                  />
                  <Text size="small" isBold={update.id === selection}>
                    {formatUpdateDate(update.created_at)}
                  </Text>
                </HistoryRow>
              ))}
            </Box>
          </Box>
        )}

        <UpdateBox minHeight={showList ? minHeight : undefined}>
          {selected ? (
            <EditableUpdatePost
              key={selected.id}
              editable={canEditUpdate(workstream, selected)}
              update={selected}
              dri={getWorkstreamDri(workstream)}
            />
          ) : (
            <NextUpdatePrompt
              title={nextTitle}
              overdue={daysOverdue > 0}
              status={getWorkstreamUpdateVisualStatus(
                updates[0]?.status ?? workstream.status ?? 'no_status'
              )}
              onLeaveUpdate={() => onLeaveUpdate(workstream.id, {button: 'next_update_prompt'})}
            />
          )}
        </UpdateBox>
      </Box>
    </Stack>
  );
};
